import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { insightsAPI } from '../../api';
import { MuscleMap } from '../insights/MuscleMap';
import {
  Trophy, ChevronRight, X, Sparkles, Flame, Activity, Clock,
  Dumbbell, Target, CheckCircle2, RotateCcw, BarChart3
} from 'lucide-react';

/**
 * SessionComplete — full-screen celebration modal shown after the
 * user logs a workout. The "wow moment" that closes the loop on
 * the entire gym flow:
 *
 *   1. User logs a workout (sets × reps × weight per exercise)
 *   2. We fetch /api/insights/muscle-engagement?days=1 to get the
 *      just-completed workout's contribution to the muscle map
 *   3. We render a Performance Club dark modal with:
 *      - "Session complete" hero
 *      - The MuscleMap lit up with the muscles they hit
 *      - Stat row: total volume, sets, duration
 *      - PR callouts (from log response)
 *      - "Log another" / "View insights" / "Done" actions
 *
 * Props:
 *   log        — the WorkoutLogResponse from /api/workouts/logs
 *   onClose    — dismiss handler (parent re-fetches data)
 *   onLogAnother — optional handler to re-open the log form
 */
export const SessionComplete = ({ log, onClose, onLogAnother }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMuscles, setShowMuscles] = useState(true);

  // Fetch today's muscle engagement so the map can render the
  // just-completed workout's contribution.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await insightsAPI.getMuscleEngagement(1);
        if (!cancelled) setEngagement(res.data);
      } catch (err) {
        console.error('Failed to load muscle engagement:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Count PRs from the log response (any exercise with pr_achieved)
  const prsAchieved = useMemo(() => {
    if (!log?.exercises) return [];
    return log.exercises.filter((ex) => ex.pr_achieved);
  }, [log]);

  // Total volume + sets computed locally (in case backend omits them)
  const stats = useMemo(() => {
    let totalSets = 0;
    let totalReps = 0;
    let totalVolume = 0;
    for (const ex of log?.exercises || []) {
      const reps = Array.isArray(ex.actual_reps) ? ex.actual_reps : [];
      const weights = Array.isArray(ex.actual_weight) ? ex.actual_weight : [];
      for (let i = 0; i < reps.length; i++) {
        const r = parseInt(reps[i]) || 0;
        const wStr = (weights[i] || '').toString().toLowerCase();
        let w = 0;
        const m = wStr.match(/[\d.]+/);
        if (m && !['bodyweight', 'bw', 'body', ''].includes(wStr.trim())) {
          w = parseFloat(m[0]) || 0;
        }
        totalSets += 1;
        totalReps += r;
        totalVolume += r * w;
      }
    }
    return {
      sets: totalSets,
      reps: totalReps,
      volume: totalVolume,
      duration: log?.duration_minutes ?? 0,
    };
  }, [log]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      data-testid="session-complete"
    >
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl border border-[var(--color-border)] bg-[#0a1208] shadow-2xl"
        style={{
          background: 'radial-gradient(circle at top, #0d1512 0%, #060b0a 70%)',
        }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-xl text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.04]"
          aria-label="Close"
          data-testid="session-complete-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Hero */}
        <div className="relative px-6 pt-8 pb-4 text-center">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-primary-200/8 rounded-full blur-3xl pointer-events-none" />
          <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-300 text-[#0a1208] mb-4">
            <Trophy className="w-8 h-8" />
          </div>
          <span className="hero-kicker">
            {t('sessionComplete.kicker', { defaultValue: 'Session complete' })}
          </span>
          <h2 className="mt-1 font-display text-3xl uppercase text-[var(--color-text)]">
            {log?.workout_name || t('sessionComplete.title', { defaultValue: 'Workout logged' })}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {coachMessage(prsAchieved.length, engagement, t)}
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-6 mb-4" data-testid="session-complete-stats">
          <StatTile
            label={t('sessionComplete.volume', { defaultValue: 'Volume' })}
            value={Math.round(stats.volume).toLocaleString()}
            unit="kg·reps"
            icon={Dumbbell}
            color="primary"
          />
          <StatTile
            label={t('sessionComplete.sets', { defaultValue: 'Sets' })}
            value={stats.sets}
            unit={t('sessionComplete.total', { defaultValue: 'total' })}
            icon={Activity}
            color="blue"
          />
          <StatTile
            label={t('sessionComplete.reps', { defaultValue: 'Reps' })}
            value={stats.reps}
            unit={t('sessionComplete.total', { defaultValue: 'total' })}
            icon={Target}
            color="purple"
          />
          <StatTile
            label={t('sessionComplete.duration', { defaultValue: 'Duration' })}
            value={formatDuration(stats.duration)}
            unit={t('sessionComplete.minutes', { defaultValue: 'minutes' })}
            icon={Clock}
            color="amber"
          />
        </div>

        {/* PR callouts */}
        {prsAchieved.length > 0 && (
          <div className="px-6 mb-4" data-testid="session-complete-prs">
            <div className="rounded-2xl border border-primary-200/30 bg-gradient-to-br from-primary-200/10 to-transparent p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-primary-200" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-primary-200 font-semibold">
                  {t('sessionComplete.prs', { defaultValue: 'Personal records' })}
                </span>
              </div>
              <ul className="space-y-1.5">
                {prsAchieved.map((pr) => (
                  <li
                    key={pr.exercise_id}
                    className="flex items-center gap-3 text-sm"
                    data-testid={`pr-callout-${pr.exercise_id}`}
                  >
                    <CheckCircle2 className="w-4 h-4 text-primary-200 shrink-0" />
                    <span className="flex-1 text-[var(--color-text)] font-medium">
                      {pr.exercise_name}
                    </span>
                    <span className="font-display text-primary-100">+{pr.improvement ?? 0}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Muscle map */}
        <div className="px-6 mb-4">
          <button
            type="button"
            onClick={() => setShowMuscles((v) => !v)}
            className="w-full flex items-center justify-between gap-2 mb-3"
            data-testid="toggle-muscles"
          >
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-primary-200" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--color-text)] font-semibold">
                {t('sessionComplete.muscles', { defaultValue: 'What you trained' })}
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
              {showMuscles ? '—' : '+'}
            </span>
          </button>
          {showMuscles && (
            <div
              className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-4"
              data-testid="session-complete-musclemap"
            >
              {loading ? (
                <div className="flex items-center justify-center h-48 text-[var(--color-text-muted)] text-sm">
                  {t('sessionComplete.loadingMap', { defaultValue: 'Lighting up your body map…' })}
                </div>
              ) : (
                <MuscleMap engagement={engagement?.groups || []} compact={false} height={320} />
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {onLogAnother && (
            <button
              type="button"
              onClick={onLogAnother}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm font-medium hover:bg-white/[0.08] transition-colors"
              data-testid="session-complete-log-another"
            >
              <RotateCcw className="w-4 h-4" />
              {t('sessionComplete.logAnother', { defaultValue: 'Log another' })}
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/insights')}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm font-medium hover:bg-white/[0.08] transition-colors"
            data-testid="session-complete-view-insights"
          >
            <BarChart3 className="w-4 h-4" />
            {t('sessionComplete.viewInsights', { defaultValue: 'View insights' })}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-300 text-[#0a1208] text-sm font-semibold hover:from-primary-300 hover:to-primary-200 transition-all"
            data-testid="session-complete-done"
          >
            {t('sessionComplete.done', { defaultValue: 'Done' })}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ===========================================================================
// Sub-components
// ===========================================================================

function StatTile({ label, value, unit, icon: Icon, color = 'primary' }) {
  const colorClasses = {
    primary: 'border-[rgba(213,255,123,0.18)] bg-[rgba(213,255,123,0.05)] text-primary-100',
    blue: 'border-[rgba(95,180,255,0.18)] bg-[rgba(95,180,255,0.05)] text-blue-200',
    purple: 'border-[rgba(195,140,255,0.18)] bg-[rgba(195,140,255,0.05)] text-purple-200',
    amber: 'border-[rgba(255,210,125,0.18)] bg-[rgba(255,210,125,0.05)] text-amber-200',
  };
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-3">
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded-lg border ${colorClasses[color]}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] truncate">
          {label}
        </span>
      </div>
      <div className="font-display text-2xl text-[var(--color-text)] leading-none">
        {value}
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
        {unit}
      </div>
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

function coachMessage(prCount, engagement, t) {
  if (prCount > 0) {
    return t('sessionComplete.coachPRs', {
      defaultValue: `${prCount} personal record${prCount > 1 ? 's' : ''} smashed. The bar remembers.`,
    });
  }
  const trained = engagement?.summary?.muscles_trained || 0;
  if (trained >= 4) {
    return t('sessionComplete.coachFull', {
      defaultValue: `Full body hit — ${trained} muscle groups trained. Recovery is the next workout.`,
    });
  }
  if (trained >= 2) {
    return t('sessionComplete.coachPartial', {
      defaultValue: `Solid session. Body is learning.`,
    });
  }
  return t('sessionComplete.coachDone', {
    defaultValue: `Logged. Consistency is the muscle that never fatigues.`,
  });
}

function formatDuration(minutes) {
  if (!minutes) return '—';
  if (minutes < 60) return minutes;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export default SessionComplete;
